import { useTranslation } from '@kostbar/i18n'
import type { AffectedOrder } from '@/api/endpoints/inventory-deliveries'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface AffectedOrdersDialogProps {
  orders: Array<AffectedOrder> | null
  onClose: () => void
}

/**
 * Shown *after* booking, a downward edit or a cancellation — never before. The goods are not
 * coming regardless of whether the system refuses the click, so this only reports which
 * pre-orders are now uncovered; the single button acknowledges, it does not confirm.
 */
export function AffectedOrdersDialog({
  orders,
  onClose,
}: AffectedOrdersDialogProps) {
  const { t } = useTranslation('inventory-deliveries')

  return (
    <AlertDialog
      open={orders != null && orders.length > 0}
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('affected.title')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2">
              <p>{t('affected.body')}</p>
              <ul className="flex flex-col gap-1">
                {orders?.map((o) => (
                  <li key={o.orderId} className="text-sm">
                    {o.pickupCode ?? o.orderId}
                    {o.pickupAt
                      ? ` — ${new Date(o.pickupAt).toLocaleString()}`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>
            {t('affected.acknowledge')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
